import React, { useState, useEffect } from 'react';
import { useProject } from '../../../contexts/ProjectContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTag, faPercent, faCalendarAlt, faMoneyBillWave } from '@fortawesome/free-solid-svg-icons';
import brandConfig from '../../../config/brandConfig';
import { getCurrencySymbol } from '../../../utils/countryUtils';

const OffersModal = ({ isOpen, onClose }) => {
  const { project } = useProject();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !project?.id) return;

    const fetchOffers = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${brandConfig.apiUrl}/offers/public/by-project/${project.id}`
        );
        if (!response.ok) throw new Error('Error al cargar ofertas');
        const data = await response.json();
        setOffers(data.data || []);
      } catch (err) {
        setError(err.message);
        setOffers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, [isOpen, project?.id]);

  if (!isOpen) return null;

  const currencySymbol = getCurrencySymbol(project?.currency);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getPurchaseTypeLabel = (type) => {
    if (!type) return 'Todos';
    const labels = {
      directa: 'Compra Directa',
      financiamiento: 'Financiamiento',
      ambos: 'Todos',
    };
    return labels[type] || type;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-theme-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-50 to-brand-100 border-b border-gray-200 px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-brand-500 flex items-center justify-center flex-shrink-0">
                <FontAwesomeIcon icon={faTag} className="text-white text-xs" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-semibold text-gray-900 truncate">Ofertas y Promociones</h2>
                <p className="text-[10px] text-gray-600 truncate">{project?.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-md hover:bg-white/70 flex items-center justify-center transition-colors text-gray-600 hover:text-gray-900 flex-shrink-0 ml-2"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-3 space-y-2.5">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
            </div>
          )}

          {error && (
            <div className="text-center py-8">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && offers.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <FontAwesomeIcon icon={faTag} className="text-gray-400 text-2xl" />
              </div>
              <p className="text-sm font-medium text-gray-700">No hay ofertas disponibles</p>
              <p className="text-xs text-gray-500 mt-1">Vuelve pronto para ver nuevas promociones</p>
            </div>
          )}

          {!loading && offers.map((offer) => (
            <div key={offer.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-white">
              {/* Offer card header */}
              <div className="bg-brand-50 border-b border-brand-100 px-4 py-2.5 flex items-center justify-between">
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <FontAwesomeIcon icon={faTag} className="text-brand-600 text-xs flex-shrink-0" />
                  <h3 className="text-sm font-semibold text-gray-900 truncate">{offer.name}</h3>
                </div>
                <div className="bg-success-50 border border-success-200 rounded-full px-2.5 py-0.5 ml-2 flex-shrink-0">
                  {offer.discount_type === 'percentage' ? (
                    <span className="text-xs font-bold text-success-700">
                      {Number(offer.discount_value)}% Descuento
                    </span>
                  ) : (
                    <span className="text-xs font-bold text-success-700">
                      -{currencySymbol} {Number(offer.discount_value).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>

              {/* Offer card body */}
              <div className="px-4 py-3 space-y-2">
                {offer.description && (
                  <p className="text-xs text-gray-700 leading-relaxed">{offer.description}</p>
                )}

                <div className="flex flex-wrap gap-2">
                  {/* Discount type */}
                  <div className="flex items-center space-x-1 text-[10px] text-gray-600 bg-gray-50 rounded px-2 py-1">
                    <FontAwesomeIcon icon={offer.discount_type === 'percentage' ? faPercent : faMoneyBillWave} className="text-brand-600 text-[9px]" />
                    <span>
                      {offer.discount_type === 'percentage'
                        ? `${Number(offer.discount_value)}% de descuento`
                        : `${currencySymbol} ${Number(offer.discount_value).toLocaleString()} de descuento`}
                    </span>
                  </div>

                  {/* Validity dates */}
                  <div className="flex items-center space-x-1 text-[10px] text-gray-600 bg-gray-50 rounded px-2 py-1">
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-brand-600 text-[9px]" />
                    <span>Hasta {formatDate(offer.end_date)}</span>
                  </div>

                  {/* Purchase type */}
                  {offer.applicable_to_purchase_type && offer.applicable_to_purchase_type !== 'ambos' && (
                    <div className="flex items-center space-x-1 text-[10px] text-gray-600 bg-blue-50 rounded px-2 py-1">
                      <span>Aplica: {getPurchaseTypeLabel(offer.applicable_to_purchase_type)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-4 py-2">
          <button
            onClick={onClose}
            className="w-full px-3 py-1.5 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors text-xs font-semibold"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default OffersModal;
